import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountRow } from "./AccountRow";

export function ContactDialog({ user, onDone }) {
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState("");

  const reset = () => { setStep(1); setOtp(""); setDemo(""); setMobile(user?.mobile || ""); };

  useEffect(() => { if (open) setMobile(user?.mobile || ""); }, [open]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/mobile/request-otp", { mobile });
      setDemo(data.demo_otp || "");
      setStep(2);
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/me/mobile/verify-otp", { mobile, otp });
      toast.success("Mobile number updated");
      await refreshUser();
      await onDone?.();
      setOpen(false);
      reset();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button className="w-full" data-testid="open-contact-dialog">
          <AccountRow icon={Phone} title="Contact Number" value={user?.mobile ? `+91 ${user.mobile}` : "Add a mobile number"} testid="account-row-contact" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{user?.mobile ? "Update Mobile Number" : "Add Mobile Number"}</DialogTitle></DialogHeader>
        {step === 1 ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Mobile number</Label>
              <div className="flex items-center gap-2">
                <span className="h-10 px-3 flex items-center rounded-md border border-border bg-slate-50 text-sm text-slate-600">+91</span>
                <Input inputMode="numeric" maxLength={10} placeholder="10-digit number" data-testid="mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} />
              </div>
              <p className="text-xs text-slate-400">We'll send a one-time password (OTP) to verify this number.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Enter the 6-digit OTP sent to <strong>+91 {mobile}</strong>.</p>
            {demo && <p className="text-xs text-gold bg-gold/10 border border-gold/30 rounded-md px-3 py-2" data-testid="demo-otp">Demo mode — your OTP is <strong>{demo}</strong></p>}
            <div className="space-y-1.5">
              <Label>OTP</Label>
              <Input inputMode="numeric" maxLength={6} placeholder="••••••" data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
            </div>
            <button className="text-xs text-slate-500 underline" onClick={() => setStep(1)} data-testid="change-mobile-btn">Change number</button>
          </div>
        )}
        <DialogFooter>
          {step === 1 ? (
            <button data-testid="request-otp-btn" onClick={requestOtp} disabled={busy || mobile.length !== 10}
              className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
              {busy ? "Sending…" : "Send OTP"}
            </button>
          ) : (
            <button data-testid="verify-otp-btn" onClick={verify} disabled={busy || otp.length !== 6}
              className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & save"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
